import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { StackInformationDialogService } from '@data-terminal/feature-dialogs/services/job-flow/stack-information-dialog.service';
import { JobService } from '@data-terminal/feature-workstation-details/services/job/job.service';
import { Job } from '@data-terminal/shared-models/job';
import { LoadingIndicatorComponentModule } from '@data-terminal/ui-presentational/loading-indicator';
import { valueWhitelistedValidator } from '@data-terminal/utils/form';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, Subscription, switchMap, take } from 'rxjs';

interface JobSelectionForm {
    jobId: FormControl<string>;
}

@Component({
    selector: 'data-terminal-request-stack-information-dialog',
    imports: [
        CommonModule,
        MatButtonModule,
        MatInputModule,
        TranslateModule,
        MatDividerModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        LoadingIndicatorComponentModule,
    ],
    templateUrl: './request-stack-information-dialog.component.html',
    styleUrl: './request-stack-information-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestStackInformationDialogComponent implements OnInit, OnDestroy {
    readonly #stackInformationDialogService = inject(StackInformationDialogService);
    readonly #dialogRef = inject(MatDialogRef<RequestStackInformationDialogComponent>);
    readonly #jobService = inject(JobService);
    readonly form = new FormGroup<JobSelectionForm>({
        jobId: new FormControl('', {
            nonNullable: true,
            validators: [Validators.required, valueWhitelistedValidator([])],
        }),
    });
    jobSuggestions$: Observable<Job[]> = of([]);
    readonly #subscription = new Subscription();
    readonly #minJobIdLength = 3;

    ngOnInit(): void {
        this.jobSuggestions$ = this.form.controls.jobId.valueChanges.pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            switchMap((jobId) =>
                jobId.length >= this.#minJobIdLength
                    ? this.#jobService.getJobList(jobId).pipe(catchError(() => of([])))
                    : of([])
            )
        );
        this.#subscription.add(
            this.jobSuggestions$.subscribe((jobs) => {
                this.form.controls.jobId.setValidators([
                    Validators.required,
                    valueWhitelistedValidator(jobs.map((job) => job.jobId)),
                ]);
                this.form.controls.jobId.updateValueAndValidity({ emitEvent: false });
            })
        );
    }

    ngOnDestroy(): void {
        this.#subscription.unsubscribe();
    }
    cancel(): void {
        this.#dialogRef.close();
    }

    requestStackInformation(): void {
        this.#dialogRef.close();

        this.#stackInformationDialogService
            .requestStackInformation(this.form.controls.jobId.value)
            .pipe(take(1))
            .subscribe(() => {});
    }
}
